import React, { useCallback } from 'react';

// Preload critical hero image
const preloadHeroImage = () => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = '/img/hero-bg.jpg';
  document.head.appendChild(link);
};

// Call preload immediately
if (typeof window !== 'undefined') {
  preloadHeroImage();
}

const Hero = () => {
  // Memoize scroll functions for better performance
  const scrollToMenu = useCallback(() => {
    const menuSection = document.getElementById('menu');
    if (menuSection) {
      menuSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const scrollToAbout = useCallback(() => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToAbout();
    }
  }, [scrollToAbout]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden transform-gpu">
      {/* Enhanced Background with Parallax Effect */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-110 transition-transform duration-[25s] ease-out animate-parallax-slow"
        style={{
          backgroundImage: 'url("/img/hero-bg.jpg")',
          filter: 'brightness(0.9) contrast(1.1)'
        }}
      >
        {/* Multi-layered gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/70 via-primary-800/30 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/20 via-transparent to-primary-900/20"></div>
        
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      
      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-accent-warm/30 rounded-full animate-float-slow"></div>
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-primary-300/20 rounded-full animate-float-medium"></div>
        <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-accent-gold/40 rounded-full animate-float-fast"></div>
      </div>
      
      {/* Enhanced Content with Better Typography */}
      <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Premium Badge */}
        <div className="inline-flex items-center px-6 py-3 mb-8 glass-strong rounded-full animate-fade-in-down">
          <svg className="w-5 h-5 text-accent-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-sm font-semibold tracking-wider text-white/90">Authentisch Mediterran</span>
        </div>
        
        <h1 className="heading-primary mb-6 animate-fade-in-up" 
            style={{
              textShadow: '3px 6px 12px rgba(0,0,0,0.8), 0 0 30px rgba(0,0,0,0.4)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f9f0 50%, #ffffff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
          Frische Pasta<br />
          <span className="text-gradient-warm">& Salate</span>
        </h1>
        
        <div className="w-24 h-1 bg-gradient-to-r from-accent-warm via-accent-gold to-accent-copper mx-auto mb-8 rounded-full animate-fade-in-up animation-delay-200"></div>
        
        <p className="text-premium mb-4 text-white/90 max-w-3xl mx-auto animate-fade-in-up animation-delay-300" 
           style={{
             textShadow: '2px 4px 8px rgba(0,0,0,0.9)'
           }}>
          Authentische mediterrane Küche im Herzen von Fulda
        </p>
        
        <p className="text-lg text-white/80 max-w-2xl mx-auto mb-16 animate-fade-in-up animation-delay-500" 
           style={{
             textShadow: '1px 2px 4px rgba(0,0,0,0.8)'
           }}>
          Erleben Sie die Tradition und den Geschmack des Mittelmeers
        </p>
        
        {/* Premium CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-8 justify-center items-center animate-fade-in-up animation-delay-500">
          <button 
            onClick={scrollToMenu}
            className="btn-primary group relative overflow-hidden min-w-[220px] shadow-glow hover:shadow-glow-lg"
            style={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Menü entdecken
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>
          
          <a 
            href="tel:4966195258092"
            className="btn-secondary group relative overflow-hidden min-w-[220px] glass-strong hover:shadow-warm"
            style={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              <svg className="w-5 h-5 group-hover:rotate-12 group-hover:scale-110 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Jetzt reservieren
              <div className="w-2 h-2 bg-accent-gold rounded-full animate-glow-pulse"></div>
            </span>
          </a>
        </div>
      </div>
      
      {/* Premium Scroll Indicator */}
       <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20">
         <div 
           className="flex flex-col items-center cursor-pointer group transition-all duration-500 hover:scale-110 focus-ring"
           onClick={scrollToAbout}
           role="button"
           tabIndex={0}
           aria-label="Scroll to next section"
           onKeyDown={handleKeyDown}
         >
           {/* Enhanced text with gradient */}
           <div className="glass rounded-full px-4 py-2 mb-4 group-hover:glass-strong transition-all duration-300">
             <span className="text-sm font-semibold tracking-wider text-white/90 group-hover:text-white transition-all duration-300" 
                   style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
               Mehr entdecken
             </span>
           </div>
           
           <div className="relative animate-bounce-gentle">
             {/* Multi-layered glow effects */}
             <div className="absolute inset-0 bg-gradient-to-b from-primary-300/40 to-primary-600/50 rounded-full blur-lg group-hover:from-accent-gold/50 group-hover:to-accent-warm/60 transition-all duration-500 scale-150 animate-glow-pulse"></div>
             <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-white/10 rounded-full blur-md group-hover:from-accent-gold/30 group-hover:to-accent-warm/20 transition-all duration-300 scale-125"></div>
             
             {/* Main indicator with enhanced styling */}
             <div className="relative glass-strong rounded-full p-4 group-hover:bg-white/20 transition-all duration-500 shadow-glow group-hover:shadow-warm transform group-hover:rotate-180">
               <svg 
                 className="w-6 h-6 text-white group-hover:text-accent-gold group-hover:translate-y-1 transition-all duration-500" 
                 fill="none" 
                 stroke="currentColor" 
                 viewBox="0 0 24 24" 
                 strokeWidth={2.5}
               >
                 <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
               </svg>
             </div>
             
             {/* Multiple pulse rings */}
             <div className="absolute inset-0 border-2 border-white/30 rounded-full animate-ping group-hover:border-accent-gold/50 animation-delay-100"></div>
             <div className="absolute inset-0 border border-white/20 rounded-full animate-ping group-hover:border-accent-warm/40 animation-delay-300 scale-125"></div>
           </div>
         </div>
       </div>
    </section>
  );
};

export default Hero;